import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';

/**
 * FrequencyBar Component for multiple choice questions
 * Uses horizontal layout for better readability
 */
const FrequencyBar = ({ data }) => {
  if (!data || data.type === 'empty') {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No data to display
      </div>
    );
  }

  // Handle frequency data (multiple choice)
  if (data.distribution) {
    const entries = Object.entries(data.distribution);
    const total = entries.reduce((sum, [, count]) => sum + count, 0);
    
    if (total === 0) {
      return (
        <div className="text-center py-4 text-muted-foreground">
          No valid responses for this question
        </div>
      );
    }
    
    const chartData = entries
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .map(([option, count]) => ({
        option: option.length > 30 ? option.substring(0, 30) + '...' : option,
        fullOption: option,
        count: count,
        percentage: Math.round((count / total) * 100)
      }));
    
    // Debug log to see what we're passing to the chart
    if (import.meta.env.DEV) {
      console.log('FrequencyBar chartData:', chartData);
    }

    const chartConfig = {
      count: {
        label: "Responses",
        color: "#3b82f6"
      }
    };

    // Use horizontal bar chart for better readability
    return (
      <div className="w-full">
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
              <XAxis 
                dataKey="option"
                className="text-muted-foreground"
                tick={{ fontSize: 11 }}
                angle={0}
                textAnchor="middle"
                interval={0}
                height={60}
              />
              <YAxis 
                className="text-muted-foreground"
                tick={{ fontSize: 12 }}
                domain={[0, Math.max(...chartData.map(d => d.count))]}
              />
              <Tooltip 
                content={function CustomTooltip({ active, payload }) {
                  CustomTooltip.displayName = 'CustomTooltip';
                  if (active && payload && payload.length > 0) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-background border rounded-lg shadow-md p-3">
                        <p className="font-medium mb-1">{data.fullOption}</p>
                        <p className="text-sm text-muted-foreground">
                          {data.count} responses ({data.percentage}%)
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar 
                dataKey="count" 
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                minPointSize={5}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Summary stats */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">Total Responses</p>
            <p className="text-lg font-semibold">{total}</p>
          </div>
          {data.mode && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Most Popular</p>
              <p className="text-sm font-medium">{data.mode}</p>
              <p className="text-xs text-muted-foreground">
                {data.distribution[data.mode]} responses
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle numeric data (opinion scale, rating)
  if (data.type === 'numeric') {
    // Create frequency distribution for numeric values
    // const responses = data.validResponses || [];
    // const frequency = {};
    
    // This would need the raw responses to create frequency, 
    // for now show summary stats
    return (
      <div className="w-full">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">Average</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {data.average || 0}
            </p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">Median</p>
            <p className="text-xl font-semibold">
              {data.median || 0}
            </p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">Min</p>
            <p className="text-xl font-semibold">
              {data.min || 0}
            </p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">Max</p>
            <p className="text-xl font-semibold">
              {data.max || 0}
            </p>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            📊 {(data.validResponses || []).length} valid numeric responses analyzed
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-4 text-muted-foreground">
      Unable to create frequency chart for this data type: {data.type}
    </div>
  );
};

export default FrequencyBar;